import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { KeyboardShortcutsModal } from '@/components/KeyboardShortcuts';
import { OnboardingWalkthrough } from '@/components/OnboardingWalkthrough';
import { GlobalSearch } from '@/components/GlobalSearch';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  TrendingUp,
  Settings,
  Bell,
  Sun,
  Moon,
  Menu,
  X,
  ChevronDown,
  LogOut,
  User,
  Sparkles,
  Search,
  PanelLeftClose,
  PanelLeft,
  Shield,
  ScrollText,
  BarChart3,
  CreditCard,
  Users,
  Activity,
  Server,
  Clock,
  ListTodo,
  FolderKanban,
  CalendarDays,
  Columns3,
  FolderOpen,
  Brain,
  MessageCircle,
  Megaphone
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = ({ collapsed }) => (
  <div className="flex items-center gap-2">
    <div className="woxora-logo text-2xl">
      <span className="wox" style={{ color: '#F2EFE9' }}>Wox</span>
      <span className="ora">ora</span>
    </div>
    {!collapsed && (
      <span className="text-[10px] uppercase tracking-[0.15em] ml-1 hidden lg:block" style={{ color: '#555', fontFamily: 'JetBrains Mono, monospace' }}>
        Where business gets done.
      </span>
    )}
  </div>
);

const navItems = [
  { 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    path: '/dashboard',
    exact: true
  },
  { 
    label: 'Work Reporter', 
    icon: FileText, 
    moduleId: 'work_reporter',
    children: [
      { label: 'Submit Report', path: '/dashboard/reports/submit' },
      { label: 'All Reports', path: '/dashboard/reports' },
      { label: 'Analytics', path: '/dashboard/reports/analytics' },
    ]
  },
  { 
    label: 'Complaints', 
    icon: MessageSquare, 
    moduleId: 'complaints',
    path: '/dashboard/complaints'
  },
  { 
    label: 'Sales', 
    icon: TrendingUp, 
    moduleId: 'sales',
    path: '/dashboard/sales'
  },
  { 
    label: 'Attendance', 
    icon: Clock, 
    moduleId: 'attendance',
    path: '/dashboard/attendance'
  },
  { 
    label: 'Tasks', 
    icon: ListTodo, 
    moduleId: 'tasks',
    children: [
      { label: 'My Tasks', path: '/dashboard/tasks/my' },
      { label: 'All Tasks', path: '/dashboard/tasks/all' },
      { label: 'Projects', path: '/dashboard/tasks/projects' },
      { label: 'Kanban Board', path: '/dashboard/tasks/kanban' },
      { label: 'Calendar', path: '/dashboard/tasks/calendar' },
      { label: 'Analytics', path: '/dashboard/tasks/analytics' },
      { label: 'Time Logs', path: '/dashboard/tasks/time-logs' },
    ]
  },
  { 
    label: 'Documents', 
    icon: FolderOpen, 
    moduleId: 'documents',
    children: [
      { label: 'All Documents', path: '/dashboard/documents' },
      { label: 'My Documents', path: '/dashboard/documents/my' },
      { label: 'Shared with Me', path: '/dashboard/documents/shared' },
      { label: 'Templates', path: '/dashboard/documents/templates' },
      { label: 'Trash', path: '/dashboard/documents/trash' },
      { label: 'Settings', path: '/dashboard/documents/settings' },
    ]
  },
  { 
    label: 'AI Insights', 
    icon: Brain, 
    moduleId: 'ai_insights',
    children: [
      { label: 'Overview', path: '/dashboard/ai-insights' },
      { label: 'Team Performance', path: '/dashboard/ai-insights/team' },
      { label: 'Sales Intelligence', path: '/dashboard/ai-insights/sales' },
      { label: 'Complaints', path: '/dashboard/ai-insights/complaints' },
      { label: 'Attendance', path: '/dashboard/ai-insights/attendance' },
      { label: 'Tasks', path: '/dashboard/ai-insights/tasks' },
      { label: 'Weekly Report', path: '/dashboard/ai-insights/weekly-report' },
      { label: 'Ask AI', path: '/dashboard/ai-insights/chat' },
      { label: 'Settings', path: '/dashboard/ai-insights/settings' },
    ]
  },
  { 
    label: 'Notice Board', 
    icon: Megaphone, 
    moduleId: 'notice_board',
    path: '/dashboard/noticeboard'
  },
  { 
    label: 'Team', 
    icon: Users, 
    moduleId: 'team',
    path: '/dashboard/team'
  },
  { 
    label: 'Activity Feed', 
    icon: Activity, 
    path: '/dashboard/activity'
  },
  { 
    label: 'Analytics', 
    icon: BarChart3, 
    moduleId: 'analytics',
    path: '/dashboard/analytics'
  },
  { 
    label: 'Audit Logs', 
    icon: ScrollText, 
    moduleId: 'analytics',
    path: '/dashboard/audit-logs'
  },
  { 
    label: 'Security', 
    icon: Shield, 
    path: '/dashboard/security'
  },
  { 
    label: 'Billing', 
    icon: CreditCard, 
    moduleId: 'billing',
    path: '/dashboard/billing'
  },
  { 
    label: 'Notifications', 
    icon: Bell, 
    path: '/dashboard/notifications'
  },
  { 
    label: 'Status', 
    icon: Server, 
    path: '/dashboard/status'
  },
  { 
    label: 'Settings', 
    icon: Settings, 
    path: '/dashboard/settings'
  },
];

const SidebarNavItem = ({ item, collapsed }) => {
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  
  const isActive = item.exact 
    ? location.pathname === item.path 
    : location.pathname.startsWith(item.path);
  
  const hasChildren = item.children && item.children.length > 0;
  const childActive = hasChildren && item.children.some(child => location.pathname === child.path);

  useEffect(() => {
    if (childActive) setExpanded(true);
  }, [childActive]);

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`sidebar-nav-item w-full justify-between ${childActive ? 'text-white' : ''}`}
          data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
        >
          <span className="flex items-center gap-3">
            <item.icon className="w-5 h-5" />
            {!collapsed && <span className="font-medium">{item.label}</span>}
          </span>
          {!collapsed && (
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          )}
        </button>
        
        {expanded && !collapsed && (
          <div className="ml-8 mt-1 space-y-1">
            {item.children.map(child => (
              <NavLink
                key={child.path}
                to={child.path}
                className={({ isActive }) => 
                  `block px-3 py-2 rounded-lg text-sm transition-colors duration-200 ${
                    isActive 
                      ? 'text-white bg-[#E06D53]' 
                      : 'text-[#A3A3A0] hover:text-[#F2EFE9] hover:bg-white/5'
                  }`
                }
                data-testid={`nav-${child.label.toLowerCase().replace(' ', '-')}`}
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.exact}
      className={({ isActive }) => 
        `sidebar-nav-item ${isActive ? 'active' : ''}`
      }
      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
    >
      <item.icon className="w-5 h-5" />
      {!collapsed && <span className="font-medium">{item.label}</span>}
    </NavLink>
  );
};

const MobileNav = () => {
  const location = useLocation();
  
  const mobileItems = [
    { label: 'Home', icon: LayoutDashboard, path: '/dashboard' },
    { label: 'Reports', icon: FileText, path: '/dashboard/reports' },
    { label: 'Complaints', icon: MessageSquare, path: '/dashboard/complaints' },
    { label: 'Sales', icon: TrendingUp, path: '/dashboard/sales' },
    { label: 'Settings', icon: Settings, path: '/dashboard/settings' },
  ];

  return (
    <nav className="mobile-nav md:hidden">
      {mobileItems.map(item => {
        const isActive = item.path === '/dashboard' 
          ? location.pathname === '/dashboard'
          : location.pathname.startsWith(item.path);
        
        return (
          <NavLink
            key={item.path}
            to={item.path}
            className={`mobile-nav-item ${isActive ? 'active' : ''}`}
            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
          >
            <item.icon />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [allowedModules, setAllowedModules] = useState(null);

  useEffect(() => {
    fetchNotifications();
    fetchMyModules();
    const hasSeenOnboarding = localStorage.getItem('woxora_onboarding_done');
    if (!hasSeenOnboarding) {
      setOnboardingOpen(true);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setShortcutsOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleAiQuery = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/query`, 
        { query: aiQuery },
        { withCredentials: true }
      );
      setAiResponse(response.data.response);
    } catch (error) {
      setAiResponse('Sorry, I could not process your query. Please try again.');
    }
    setAiLoading(false);
  };

  const fetchMyModules = async () => {
    try {
      const res = await axios.get(`${API}/settings/my-modules`, { withCredentials: true });
      setAllowedModules(res.data.modules || []);
    } catch (e) {
      // Fallback: show all modules if endpoint fails
      setAllowedModules(null);
    }
  };

  const filteredNavItems = allowedModules
    ? navItems.filter(item => !item.moduleId || allowedModules.includes(item.moduleId))
    : navItems;

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Desktop */}
      <aside 
        className={`fixed left-0 top-0 h-screen z-40 transition-all duration-300 hidden md:flex flex-col ${
          sidebarCollapsed ? 'w-20' : 'w-60'
        }`}
        style={{ backgroundColor: '#0A0A0B' }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-6" style={{ borderBottom: '1px solid #242426' }}>
          <Logo collapsed={sidebarCollapsed} />
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="transition-colors duration-200"
            style={{ color: '#555' }}
            onMouseEnter={e => e.currentTarget.style.color = '#F2EFE9'}
            onMouseLeave={e => e.currentTarget.style.color = '#555'}
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {filteredNavItems.map(item => (
            <SidebarNavItem key={item.label} item={item} collapsed={sidebarCollapsed} />
          ))}
        </nav>

        {/* AI Assistant Button - Fixed at bottom */}
        {!sidebarCollapsed && (
          <div className="flex-shrink-0 p-4">
            <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(224, 109, 83, 0.1)', border: '1px solid rgba(224, 109, 83, 0.2)' }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="ai-dot" />
                <span className="text-xs font-semibold" style={{ color: '#E06D53' }}>AI Assistant</span>
              </div>
              <p className="text-xs" style={{ color: '#777' }}>Ask anything about your business data</p>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-60'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-[#0A0A0B]/80 backdrop-blur-xl border-b border-border">
          <div className="flex items-center justify-between h-16 px-4 md:px-6">
            {/* Mobile menu */}
            <div className="md:hidden">
              <Logo collapsed={false} />
            </div>

            {/* Global Search Bar */}
            <div className="hidden md:flex items-center flex-1 max-w-xl mx-4">
              <button
                onClick={() => setSearchOpen(true)}
                className="relative w-full flex items-center"
                data-testid="global-search-trigger"
              >
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <div className="w-full pl-10 pr-4 h-10 flex items-center bg-secondary border border-border rounded-md text-sm text-muted-foreground">
                  Search everything...
                  <kbd className="ml-auto pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 text-[10px] text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    <span className="text-xs">Ctrl</span>K
                  </kbd>
                </div>
              </button>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-slate-600 dark:text-slate-400"
                data-testid="theme-toggle"
              >
                {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-semibold">Notifications</h3>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.slice(0, 5).map(notif => (
                      <DropdownMenuItem key={notif.notification_id} className="p-3">
                        <div>
                          <p className="font-medium text-sm">{notif.title}</p>
                          <p className="text-xs text-slate-500">{notif.message}</p>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.avatar_url} />
                      <AvatarFallback className="text-white text-sm" style={{ backgroundColor: '#E06D53' }}>
                        {getInitials(user?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">{user?.full_name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                    <p className="font-semibold">{user?.full_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                    <Badge variant="secondary" className="mt-2 text-xs capitalize">
                      {user?.role?.replace('_', ' ')}
                    </Badge>
                  </div>
                  <DropdownMenuItem asChild>
                    <NavLink to="/dashboard/settings" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Profile Settings
                    </NavLink>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* AI Response */}
          {aiResponse && (
            <div className="px-4 md:px-6 pb-4">
              <div className="rounded-lg p-4 border" style={{ backgroundColor: 'rgba(224, 109, 83, 0.06)', borderColor: 'rgba(224, 109, 83, 0.15)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#E06D53' }}>
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{aiResponse}</p>
                    <button 
                      onClick={() => setAiResponse(null)}
                      className="text-xs mt-2 hover:underline"
                      style={{ color: '#E06D53' }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 main-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile Navigation */}
      <MobileNav />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} />

      {/* Global Search */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Onboarding Walkthrough */}
      <OnboardingWalkthrough 
        open={onboardingOpen} 
        onComplete={() => {
          setOnboardingOpen(false);
          localStorage.setItem('woxora_onboarding_done', 'true');
        }} 
      />
    </div>
  );
}
